import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import Store from '@app/store';
import PreviewHelper from '@app/helper/preview';

import Router from '@module/router';
import I18n from '@module/i18n';
import { canBeShownAsGrid } from '@app/transfer-bundle/helpers/preview';

import Action from '@component/preview/action';
import FileWrapper from '@component/preview/file-wrapper';
import Icon from '@component/icon';
import NotSupported from '@component/preview/file-types/not-supported';

import { ABBEY } from '@constant/colors';
import { TransferDownloadActions } from '@app/transfer-bundle/store/download-actions';

export default class Preview extends React.Component {
  static propTypes = {
    page: PropTypes.object,
    route: PropTypes.object,
    setReturnPath: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.clickOnNextArrow = this.clickOnNextArrow.bind(this);
    this.clickOnPreviousArrow = this.clickOnPreviousArrow.bind(this);
    this.onDownload = this.onDownload.bind(this);
    this.onKeyNavigation = this.onKeyNavigation.bind(this);
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyNavigation, false);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyNavigation, false);
  }

  componentDidUpdate() {
    const {
      transfer: { items },
    } = Store.getState();
    const downloadUrl = this.props.route.path.split('/preview')[0];
    if (canBeShownAsGrid(items)) {
      /**
       * HACK: we need to set the return path based on state, that's why it's set here
       * and not in componentDidMount for example. Also, setting the return path
       * triggers a prop state change, which will eventually update the return path, which will...
       * If we remove the if guard, we will end up with a `Maximum call stack size exceeded` error.
       */
      if (this.props.page.state.returnPath === null) {
        this.props.setReturnPath(
          `${downloadUrl}/grid`,
          I18n.t('transfer.preview.overview')
        );
      }
    } else {
      Router.setLastRoute(
        'transfer',
        this.props.route.path.split('/preview')[0]
      );
    }
  }

  goToPreviousPreview() {
    const {
      transfer: { items },
      route: {
        params: { file_id },
      },
    } = Store.getState();
    const fileIndex = items.findIndex((file) => file.id === file_id);
    const index = fileIndex === 0 ? items.length - 1 : fileIndex - 1;
    this.goToPreview(items[index].id);
  }

  goToNextPreview() {
    const {
      transfer: { items },
      route: {
        params: { file_id },
      },
    } = Store.getState();
    const fileIndex = items.findIndex((file) => file.id === file_id);
    const index = fileIndex === items.length - 1 ? 0 : fileIndex + 1;
    this.goToPreview(items[index].id);
  }

  goToPreview(fileId) {
    const {
      route: {
        params: { id, secret, recipient_id },
      },
    } = Store.getState();

    Router.navigateTo(
      PreviewHelper.getPreviewUrl({
        transferId: id,
        fileId,
        secret,
        recipientId: recipient_id,
      })
    );
  }

  // Extra method to track this action on GA
  clickOnPreviousArrow() {
    this.goToPreviousPreview();
  }

  // Extra method to track this action on GA
  clickOnNextArrow() {
    this.goToNextPreview();
  }

  // Extra method to track this action on GA
  keyboardPreviousPreview() {
    this.goToPreviousPreview();
  }

  // Extra method to track this action on GA
  keyboardNextPreview() {
    this.goToNextPreview();
  }

  onKeyNavigation(e) {
    const { transfer } = Store.getState();

    if (transfer.items.length <= 1) return;

    const key = e.key || e.which;

    switch (key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        this.keyboardPreviousPreview();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        this.keyboardNextPreview();
        break;
      case 'Escape': {
        if (this.props.page.state.returnPath) {
          Router.navigateTo(
            PreviewHelper.getGridUrl({
              transferId: transfer.id,
              recipientId: transfer.recipient_id,
              secret: transfer.secret,
            })
          );
        }
      }
    }
  }

  onDownload(item) {
    let params = {};
    const { transfer, session } = Store.getState();

    if (this.props.route) {
      params = this.props.route.params;
    } else {
      params = Store.getState().route.params;
    }

    Store.dispatch(
      TransferDownloadActions.downloadTransferFile({
        transfer,
        secret: params.secret,
        recipientId: params.recipient_id,
        fileId: item.id,
        domainUserId: session.domainUserId,
      })
    );
  }

  renderItem(item) {
    if (!item) {
      return null;
    }

    let onClick = null;
    const action = {
      text: I18n.t('transfer.download_preview'),
      callback: () => this.onDownload(item),
    };
    const { transfer } = Store.getState();
    const file = item.file;

    if (canBeShownAsGrid(transfer.items)) {
      onClick = () =>
        PreviewHelper.getGridUrl({
          transferId: transfer.id,
          recipientId: transfer.recipient_id,
          secret: transfer.secret,
        });
    }

    if (file.previewable && !file.state.error) {
      return (
        <FileWrapper
          autoResize={false}
          item={item}
          route={this.props.route}
          onClick={onClick}
        />
      );
    }

    return (
      <div className="preview__container">
        <NotSupported extension={file.type} />
        <Action
          title={file.name}
          subTitle={I18n.size(file.size)}
          action={action}
        />
      </div>
    );
  }

  getPreviewedItem() {
    const {
      route: {
        params: { file_id },
      },
      transfer: { items },
    } = Store.getState();
    if (!items) {
      return null;
    }

    return items.find((item) => item.id === file_id);
  }

  renderArrows() {
    const {
      transfer: { items },
    } = Store.getState();
    if (Browser.isMobile || !items || items.length <= 1) {
      return null;
    }

    return [
      <div
        onClick={this.clickOnPreviousArrow}
        key="arrow-left"
        className="preview__arrow preview__arrow--left"
      >
        <Icon color={ABBEY} type="Arrow" />
      </div>,
      <div
        onClick={this.clickOnNextArrow}
        key="arrow-right"
        className="preview__arrow preview__arrow--right"
      >
        <Icon color={ABBEY} type="Arrow" orientation="right" />
      </div>,
    ];
  }

  render() {
    const item = this.getPreviewedItem();

    return (
      <div className="preview" ref={(node) => (this.node = node)}>
        {this.renderArrows()}
        {this.renderItem(item)}
      </div>
    );
  }
}
